import { useState } from 'react';
import { TResultImageSchema } from '@/schemas/vehicule-check-result/result-image.schema';

interface UseImageNavigationProps {
  images?: TResultImageSchema[];
  singleImageUrl?: string;
}

interface GetNavigationHandlersProps extends UseImageNavigationProps {
  currentImageIndex: number;
  setCurrentImageIndex: (index: number) => void;
}

interface GetImageNavigationHandlersProps extends UseImageNavigationProps {
  itemId: string;
}

const getActiveImages = (images: TResultImageSchema[]) => {
  return images?.filter((image) => !image?.isDeleted);
};

export const getNavigationHandlers = ({
  images,
  singleImageUrl,
  currentImageIndex,
  setCurrentImageIndex,
}: GetNavigationHandlersProps) => {
  const containsImageList = images?.length > 0;
  const containsMoreThanOneImage = images?.length > 1;

  const currentImageUrl = containsImageList
    ? images?.[currentImageIndex]?.url
    : singleImageUrl;

  const currentImageItem = containsImageList
    ? images?.[currentImageIndex]
    : null;

  const onLeftClick = () => {
    setCurrentImageIndex(
      currentImageIndex === 0
        ? (images?.length || 1) - 1
        : currentImageIndex - 1
    );
  };

  const onRightClick = () => {
    setCurrentImageIndex(
      currentImageIndex === (images?.length || 1) - 1
        ? 0
        : currentImageIndex + 1
    );
  };

  const onDotClick = (imageId: string) => {
    const imageIndex = images?.findIndex((image) => image?.id === imageId);
    if (imageIndex !== undefined && imageIndex !== -1) {
      setCurrentImageIndex(imageIndex);
    }
  };

  return {
    containsImageList,
    containsMoreThanOneImage,
    currentImageIndex,
    currentImageUrl,
    currentImageItem,
    onLeftClick,
    onRightClick,
    onDotClick,
  };
};

export const useImageNavigation = () => {
  const [navigatedImageIndex, setNavigatedImageIndex] = useState<
    Record<string, number>
  >({});
  const [shouldNavigateToLast, setShouldNavigateToLast] = useState<
    Record<string, boolean>
  >({});

  const getImageNavigationHandlers = ({
    itemId,
    images,
    singleImageUrl,
  }: GetImageNavigationHandlersProps) => {
    images = getActiveImages(images);
    const currentNavigatedIndex = navigatedImageIndex[itemId] || 0;
    const shouldGoToLast = shouldNavigateToLast[itemId] || false;

    const setCurrentIndexForItem = (index: number) => {
      setNavigatedImageIndex((prev) => ({
        ...prev,
        [itemId]: index,
      }));
      // Clear the "navigate to last" flag when manually navigating
      setShouldNavigateToLast((prev) => ({
        ...prev,
        [itemId]: false,
      }));
    };

    // Calculate the actual last index
    const lastIndex = Math.max(0, (images?.length || 1) - 1);

    // Use last index if flag is set, otherwise use current or adjust if out of bounds
    const isOutOfBounds = currentNavigatedIndex >= (images?.length || 0);
    const finalIndex =
      shouldGoToLast || isOutOfBounds ? lastIndex : currentNavigatedIndex;

    // Note: We don't update state during render to avoid infinite loops
    // The finalIndex calculation handles the navigation logic

    return getNavigationHandlers({
      images,
      singleImageUrl,
      currentImageIndex: finalIndex,
      setCurrentImageIndex: setCurrentIndexForItem,
    });
  };

  const navigateToNewImage = (itemId: string, images: TResultImageSchema[]) => {
    const activeImages = getActiveImages(images);
    const newImageIndex = Math.max(0, activeImages.length - 1);

    setNavigatedImageIndex((prev) => ({
      ...prev,
      [itemId]: newImageIndex,
    }));
  };

  const navigateToLastImage = (itemId: string) => {
    setShouldNavigateToLast((prev) => ({
      ...prev,
      [itemId]: true,
    }));
  };

  return {
    setNavigatedImageIndex,
    getImageNavigationHandlers,
    navigateToNewImage,
    navigateToLastImage,
  };
};
